import { zodResolver } from "@hookform/resolvers/zod";
import { CheckIcon, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { z } from "zod";
import { getErrorDetails } from "../../app/appUtils";
import AppButton from "../../components/ui/AppButton";
import AppInputField from "../../components/ui/AppInputField";
import { Spinner } from "../../components/ui/common";
import { Modal } from "../../components/ui/Modal";
import { MySelect } from "../../components/ui/Select";
import SuccessView from "../../components/ui/SuccessView";
import { useToast } from "../../hooks/useToast";
import { cn, removeAfterDash } from "../../lib/utils";
import CrewDropdownMenu from "../driver/ui/CrewDropDownMenu";
import DriverDropdownMenu from "../driver/ui/DriverDropdownMenu";
import RequisitionModal from "../requisition/RequisitionModal";
import VehicleDropdownMenu from "../vehicle/ui/VehicleDropdownMenu";
import { Maintenance, MaintenanceData } from "./@types";
import {
  useAddMaintenanceMutation,
  useUpdateMaintenanceMutation,
} from "./maintenanceApiSlice";

const crewMember = z.object({
  id: z.string().nonempty("Please select a crew member"),
  name: z.string(),
  role: z.string().nonempty("Please enter crew role"),
});

export const maintenanceFormSchema = z.object({
  vehicleId: z.string().nonempty("Vehicle ID is required"),
  status: z.string().nonempty("Status is required"),
  driverId: z.string().nonempty("Driver ID is required"),
  maintenanceType: z.string().nonempty("Maintenance type is required"),
  odometerReading: z.string().nonempty("Odometer reading is required"),
  maintenanceTeams: z.array(crewMember).min(1, "At least one item is required"),
  technicianNote: z.string().nonempty("Technician note is required"),
});

export type MaintenanceFormData = z.infer<typeof maintenanceFormSchema>;

interface MaintenanceModelProps {
  isOpen: boolean;
  onClose: () => void;
  maintenance?: Maintenance | null;
  mode?: "add" | "edit";
}

const MaintenanceModal: React.FC<MaintenanceModelProps> = ({
  isOpen,
  onClose,
  maintenance = null,
  mode = maintenance ? "edit" : "add",
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [maintenanceData, setMaintenanceData] =
    useState<MaintenanceData | null>(null);
  const [isRequisitionModalOpen, setIsRequisitionModalOpen] = useState(false);
  const [createdCardCode, setCreatedCardCode] = useState<string | null>(null);
  const toast = useToast();
  const isEditMode = mode === "edit" && maintenance;

  const [
    createMaintenance,
    {
      isLoading: isCreating,
      isSuccess: createSuccess,
      isError: createError,
      error: createErrorData,
      reset: resetCreate,
    },
  ] = useAddMaintenanceMutation();

  const [
    updateMaintenance,
    {
      isLoading: isUpdating,
      isSuccess: updateSuccess,
      isError: updateError,
      error: updateErrorData,
      reset: resetUpdate,
    },
  ] = useUpdateMaintenanceMutation();

  // Derived states for cleaner code
  const isLoading = isCreating || isUpdating;
  const isSuccess = createSuccess || updateSuccess;
  const isError = createError || updateError;
  const error = createErrorData || updateErrorData;

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    setValue,
    reset,
  } = useForm<MaintenanceFormData>({
    resolver: zodResolver(maintenanceFormSchema),
    mode: "onChange",
    defaultValues: {
      vehicleId: "",
      status: "",
      driverId: "",
      maintenanceType: "",
      odometerReading: "",
      maintenanceTeams: [
        {
          id: "",
          name: "",
          role: "",
        },
      ],
      technicianNote: "",
    },
  });

  const {
    fields: itemFields,
    append: appendItem,
    remove: removeItem,
  } = useFieldArray({
    control,
    name: "maintenanceTeams",
  });

  // Populate form with maintenance data when editing
  useEffect(() => {
    if (isEditMode && maintenance) {
      reset({
        vehicleId: maintenance.vehicle,
        status: maintenance.status,
        driverId: maintenance.driver,
        maintenanceType: maintenance.maintenanceType,
        odometerReading: maintenance.odometerReading,
        maintenanceTeams: maintenance.maintenanceTeams,
        technicianNote: maintenance.technicianNote,
      });
    } else if (!isEditMode) {
      // Reset to empty when switching to add mode
      reset({
        vehicleId: "",
        status: "",
        driverId: "",
        maintenanceType: "",
        odometerReading: "",
        maintenanceTeams: [
          {
            id: "",
            name: "",
            role: "",
          },
        ],
        technicianNote: "",
      });
    }
  }, [isEditMode, maintenance, reset]);

  const onSubmit = async (data: MaintenanceFormData) => {
    if (isSubmitting || isLoading) return;

    // Prevent submission if editing and no changes were made
    if (isEditMode && !isDirty) {
      toast.warning({
        title: "No Changes",
        message: "No changes were made to update.",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        vehicleId: data.vehicleId,
        status: data.status,
        driverId: data.driverId,
        maintenanceType: data.maintenanceType,
        odometerReading: data.odometerReading,
        maintenanceTeams: data.maintenanceTeams,
        technicianNote: data.technicianNote,
      };

      if (isEditMode && maintenance?.id) {
        await updateMaintenance({
          id: maintenance.id,
          ...payload,
        }).unwrap();
      } else {
        const res = await createMaintenance(payload).unwrap();

        setMaintenanceData(res.data);
        // Store cardCode for the success view
        if (res.data?.cardCode) {
          setCreatedCardCode(res.data.cardCode);
        }
      }

      setShowSuccess(true);
    } catch (err) {
      console.error(`${isEditMode ? "Update" : "Create"} error:`, err);
      const errorMessage =
        getErrorDetails(err).message ||
        `Failed to ${
          isEditMode ? "update" : "add"
        } maintenance. Please try again.`;

      toast.error({
        title: "Error",
        message: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (isSuccess && !showSuccess) {
      toast.success({
        title: "Success",
        message: `Maintenance ${isEditMode ? "updated" : "added"} successfully`,
      });
      setShowSuccess(true);
    }

    if (isError) {
      const errorMessage =
        getErrorDetails(error).message ||
        `Failed to ${
          isEditMode ? "update" : "add"
        } maintenance. Please try again.`;

      toast.error({
        title: "Error",
        message: errorMessage,
      });
    }
  }, [isSuccess, isError, error, toast, isEditMode, showSuccess]);

  const handleCloseModal = () => {
    setShowSuccess(false);
    setCreatedCardCode(null);
    setMaintenanceData(null);
    reset();
    resetCreate();
    resetUpdate();
    onClose();
  };

  const handleOpenRequisitionModal = () => {
    setIsRequisitionModalOpen(true);
  };

  const handleCloseRequisitionModal = () => {
    setIsRequisitionModalOpen(false);
    // Don't close maintenance modal - let user see success message
    // They can close it manually with Cancel button
  };

  const handleAddItem = () => {
    appendItem({ id: "", role: "", name: "" });
  };

  const handleRemoveItem = (index: number) => {
    if (itemFields.length > 1) {
      removeItem(index);
    } else {
      toast.warning({
        title: "Cannot Remove",
        message: "At least one item is required.",
      });
    }
  };

  const renderSuccessView = () => {
    if (showSuccess) {
      // Show custom success view for new maintenance creation with cardCode
      if (!isEditMode && createdCardCode) {
        return (
          <div className="max-w-full md:max-w-[40vw] mx-auto flex flex-col items-center justify-center p-6 sm:p-12 text-center">
            <div className="w-20 h-20 border-2 border-primary text-primary rounded-full flex items-center justify-center mb-6">
              <CheckIcon className="w-8 h-8" />
            </div>

            <h2 className="text-2xl font-semibold text-gray-900 mb-6">
              Maintenance Created Successfully
            </h2>

            <p className="text-gray-600 mb-8 max-w-md">
              A new maintenance was created and the card code is{" "}
              <span className="font-semibold text-primary">
                {createdCardCode}
              </span>
            </p>

            <div className="flex gap-4">
              <AppButton
                type="button"
                onClick={handleCloseModal}
                className="px-6 py-2 bg-[#F6F6F6] rounded-md text-hColor hover:bg-gray-50 transition-colors text-[15px]"
              >
                Cancel
              </AppButton>

              <AppButton
                type="button"
                onClick={handleOpenRequisitionModal}
                className="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary/75 transition-colors text-[15px]"
              >
                Create Requisition
              </AppButton>
            </div>
          </div>
        );
      }

      // Default success view for updates
      return (
        <SuccessView
          handleCloseModal={handleCloseModal}
          heading=""
          message={`Maintenance has been ${
            isEditMode ? "updated" : "added"
          } successfully.`}
        />
      );
    }
    return null;
  };

  const modalTitle = showSuccess
    ? ""
    : `${isEditMode ? "Edit" : "Add"} maintenance`;
  const submitButtonText = isEditMode
    ? "Update maintenance"
    : "+ Add maintenance";

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleCloseModal}
        title={modalTitle}
        className="max-w-full sm:max-w-[80vw] lg:max-w-[45vw] w-full bg-white pb-4"
      >
        {isSuccess ? (
          renderSuccessView()
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-0 p-6">
            <h1 className="text-[13px] font-semibold mb-2">Vehicle details</h1>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <Controller
                control={control}
                name="vehicleId"
                render={({ field }) => (
                  <VehicleDropdownMenu
                    value={field.value}
                    onChange={(value) =>
                      setValue("vehicleId", value, {
                        shouldValidate: true,
                      })
                    }
                    className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px] mb-6"
                    error={errors.vehicleId?.message}
                  />
                )}
              />

              <Controller
                control={control}
                name="driverId"
                render={({ field }) => (
                  <DriverDropdownMenu
                    value={field.value}
                    onChange={(value) =>
                      setValue("driverId", value, {
                        shouldValidate: true,
                      })
                    }
                    className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px] mb-6"
                    error={errors.driverId?.message}
                    placeholder="Select a driver"
                  />
                )}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <Controller
                control={control}
                name="maintenanceType"
                render={({ field }) => (
                  <AppInputField
                    label="Maintenance type"
                    type="text"
                    error={errors.maintenanceType?.message}
                    {...field}
                    className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                    placeholder="Enter maintenance type"
                  />
                )}
              />

              <Controller
                control={control}
                name="odometerReading"
                render={({ field }) => (
                  <AppInputField
                    label="Odometer"
                    type="text"
                    error={errors.odometerReading?.message}
                    {...field}
                    className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                    placeholder="Enter odometer reading"
                  />
                )}
              />
            </div>

            <div className="flex flex-col w-full py-3">
              <label className="text-[13px] pb-3">Status</label>
              <Controller
                control={control}
                name="status"
                render={({ field }) => (
                  <MySelect<string>
                    name={"name"}
                    id={"id"}
                    label={""}
                    options={["Pending", "InProgress", "Completed"]}
                    getValue={(option: string) => option}
                    getName={(option: string) => option}
                    value={field.value}
                    onChange={(e) => setValue("status", e)}
                    onValChange={(e) => setValue("status", e)}
                    placeholder={"Maintenance status"}
                    disabled={isLoading}
                    className={cn(
                      "focus:ring-primary focus:border-primary",
                      {
                        "opacity-50": isLoading,
                      },
                      "w-full bg-[#F6F6F6] border-[#F6F6F6] h-[48px] text-[13px] px-2 text-textColor appearance-none border rounded-lg focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                    )}
                  />
                )}
              />
            </div>

            <div className="">
              <h1 className="text-[13px] font-semibold py-4">
                Maintenance team
              </h1>

              <div className="p-2 border border-gray-200 rounded-lg">
                {itemFields.map((field, index) => (
                  <div key={index} className="flex gap-2 items-start">
                    <div className="w-full">
                      <Controller
                        control={control}
                        name={`maintenanceTeams.${index}.role`}
                        render={({ field }) => (
                          <AppInputField
                            label="Role"
                            type="text"
                            error={
                              errors.maintenanceTeams?.[index]?.role?.message
                            }
                            {...field}
                            className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                            placeholder="Enter item name"
                          />
                        )}
                      />
                    </div>

                    <div className="w-full">
                      <Controller
                        control={control}
                        name={`maintenanceTeams.${index}.id`}
                        render={({ field }) => (
                          <CrewDropdownMenu
                            value={field.value}
                            onChange={(value) => {
                              setValue(
                                `maintenanceTeams.${index}.id`,
                                value?.id ?? "",
                                {
                                  shouldValidate: true,
                                }
                              );
                              setValue(
                                `maintenanceTeams.${index}.name`,
                                removeAfterDash(value?.name) ?? "",
                                {
                                  shouldValidate: true,
                                }
                              );
                            }}
                            className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px] mb-6"
                            error={errors.maintenanceTeams?.message}
                            label="Crew"
                          />
                        )}
                      />
                    </div>

                    <div className="bg-red-100 h-[48px] rounded-lg flex items-center mt-8">
                      <button
                        type="button"
                        onClick={() => handleRemoveItem(index)}
                        disabled={itemFields.length === 1}
                        className="p-2 text-red-500 hover:text-red-700 disabled:text-gray-300 disabled:cursor-not-allowed transition-colors"
                        title="Remove item"
                      >
                        <Trash2 />
                      </button>
                    </div>
                  </div>
                ))}

                <div className="flex items-center">
                  <button
                    type="button"
                    onClick={handleAddItem}
                    className="text-primary text-[13px] font-semibold"
                  >
                    + Add crew member
                  </button>
                </div>
              </div>
            </div>

            <div className="pt-4">
              <Controller
                control={control}
                name="technicianNote"
                render={({ field }) => (
                  <AppInputField
                    label="Technician note"
                    type="text"
                    error={errors.technicianNote?.message}
                    {...field}
                    className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                    placeholder="Enter technician note"
                  />
                )}
              />
            </div>

            <div className="flex justify-center items-center space-x-4 text-[15px]">
              <AppButton
                type="button"
                onClick={handleCloseModal}
                disabled={isSubmitting || isLoading}
                className="px-6 py-2 bg-[#F6F6F6] rounded-md text-hColor hover:bg-gray-50 transition-colors text-[15px] disabled:opacity-50"
              >
                Cancel
              </AppButton>

              <AppButton
                type="submit"
                disabled={isSubmitting || isLoading}
                className="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary/75 transition-colors disabled:opacity-50 text-[15px]"
              >
                {isSubmitting || isLoading ? <Spinner /> : submitButtonText}
              </AppButton>
            </div>
          </form>
        )}
      </Modal>

      {isRequisitionModalOpen && createdCardCode && (
        <RequisitionModal
          isOpen={isRequisitionModalOpen}
          onClose={handleCloseRequisitionModal}
          initialCardId={createdCardCode}
        />
      )}
    </>
  );
};

export default MaintenanceModal;
